import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, query, where, collection } from 'firebase/firestore';
import { dailyActivityService } from '../services/dailyActivityService';
import { db } from '../firebase';
import ActivityDetailPane from '../components/ActivityDetailPane';
import ArtworkUploadForm from '../components/ArtworkUploadForm';
import PublicWallCarousel from '../components/PublicWallCarousel';
import ActivityWallModal from '../components/ActivityWallModal';
import BackButton from '../components/BackButton';
import { useAuth } from '../context/AuthContext';
import KidBackground from '../ui/backgrounds/KidBackground';

const ActivityPage = () => {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState('');
  const [hasParticipated, setHasParticipated] = useState(false);
  const [userSubmission, setUserSubmission] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(true);
  // Read submissionId from URL immediately to enable direct modal render without intermediate loading views
  const initialSubmissionFromQuery = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('submissionId');
    } catch (_) {
      return null;
    }
  })();
  const [wallModal, setWallModal] = useState(initialSubmissionFromQuery);

  const handleSignOut = async () => {
    try { await logout(); } catch (_) {}
    try { localStorage.removeItem('mentor_session'); window.dispatchEvent(new Event('mentor_session_changed')); } catch (_) {}
    navigate('/');
  };

  useEffect(() => {
    if (!activityId || !user) return;

    const loadActivityData = async () => {
      try {
        setLoading(true);

        // Load activity details
        const activityDoc = await getDoc(doc(db, 'activities', activityId));
        if (activityDoc.exists()) {
          setActivity({ id: activityDoc.id, ...activityDoc.data() });
        } else {
          // Fallback to dailyActivityService metadata or minimal placeholder
          try {
            const all = await dailyActivityService.getAllActivities();
            const match = all.find(a => a.id?.toString() === activityId?.toString());
            if (match) {
              setActivity({ id: match.id?.toString() || activityId, ...match });
            } else {
              setActivity({
                id: activityId,
                title: `Activity ${activityId}`,
                description: 'Daily creative activity',
                coverImageUrl: '',
                prerequisites: [],
                steps: []
              });
            }
          } catch (e) {
            // As a last resort, set a generic activity so the page renders
            setActivity({
              id: activityId,
              title: `Activity ${activityId}`,
              description: 'Daily creative activity',
              coverImageUrl: '',
              prerequisites: [],
              steps: []
            });
          }
        }

        // Participation / submission lookups are optional — do not fail the whole page
        try {
          const participantId = `${activityId}_${user.uid}`;
          const participantDoc = await getDoc(doc(db, 'activityParticipants', participantId));

          if (participantDoc.exists()) {
            setHasParticipated(true);
            setShowUploadForm(false);

            const submissionsQuery = query(
              collection(db, 'submissions'),
              where('activityId', '==', activityId),
              where('uid', '==', user.uid)
            );

            const submissionsSnapshot = await getDocs(submissionsQuery);
            if (!submissionsSnapshot.empty) {
              const userSubmissionData = submissionsSnapshot.docs[0];
              setUserSubmission({ id: userSubmissionData.id, ...userSubmissionData.data() });
            }
          }
        } catch (participationErr) {
          console.warn('[ActivityPage] participation lookup failed (non-fatal):', participationErr);
        }

      } catch (error) {
        console.error('Error loading activity:', error);
        setError('Failed to load activity');
      } finally {
        setLoading(false);
      }
    };

    loadActivityData();
  }, [activityId, user]);

  useEffect(() => {
    if (!loading) setHasLoadedOnce(true);
  }, [loading]);

  // Keep wallModal in sync if URL changes (no gating to avoid intermediate loading screens)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const submissionId = params.get('submissionId');
      if (submissionId && submissionId !== wallModal) {
        setWallModal(submissionId);
      }
    } catch (_) {}
  }, [activityId, user]);

  const handleUploadSuccess = (submissionId, imageUrl) => {
    setUserSubmission({ id: submissionId, imageUrl, isPublic: false });
    setShowUploadForm(false);
  };

  const handleUploadComplete = (isPublic) => {
    if (userSubmission) {
      setUserSubmission(prev => ({ ...prev, isPublic }));
    }
    setHasParticipated(true);
  };

  // When opened via notification (wallModal present), bypass loading screen and render only the modal
  if (wallModal) {
    return (
      <div className="min-h-screen bg-black/60">
        <div className="fixed top-4 left-4 right-4 z-[10000] flex items-center justify-between">
          <BackButton to={-1} label="Back" />
          <div className="flex items-center gap-1 md:gap-3 ml-auto">
            <button onClick={() => navigate('/')} className="bg-amber-400 hover:bg-amber-500 text-white font-bold rounded-lg md:rounded-xl shadow px-1.5 py-0.5 md:px-4 md:py-2 border-2 md:border-4 border-amber-200 text-[7px] md:text-base">Artist Corner</button>
            {(isAuthenticated || (typeof localStorage !== 'undefined' && localStorage.getItem('mentor_session')) ) && (
              <button onClick={handleSignOut} className="bg-amber-400 hover:bg-amber-500 text-white font-bold rounded-lg md:rounded-xl shadow px-1.5 py-0.5 md:px-4 md:py-2 border-2 md:border-4 border-amber-200 text-[7px] md:text-base">Sign Out</button>
            )}
          </div>
        </div>
        <ActivityWallModal
          activityId={activityId}
          initialSubmissionId={wallModal}
          onClose={() => {
            navigate(-1);
          }}
        />
      </div>
    );
  }

  if (loading && !hasLoadedOnce) {
    return (
      <div className="min-h-screen relative">
        <KidBackground variant="aurora" intensity={0.7} />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative z-10 p-6">
          <BackButton />
          <div className="max-w-7xl mx-auto mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="">
                <div className="h-8 bg-amber-200 rounded-lg mb-4"></div>
                <div className="h-64 bg-amber-200 rounded"></div>
              </div>
              <div className="">
                <div className="h-8 bg-amber-200 rounded-lg mb-4"></div>
                <div className="h-64 bg-amber-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative">
        <KidBackground variant="doodles" intensity={0.6} />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative z-10 p-6">
          <BackButton />
          <div className="max-w-7xl mx-auto mt-8">
            <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
              <div className="text-6xl mb-4">😔</div>
              <h2 className="text-2xl font-bold text-red-800 mb-2">Oops!</h2>
              <p className="text-red-700 mb-4">{error}</p>
              <button
                onClick={() => navigate('/gallery/studio')}
                className="bg-red-500 text-white font-semibold py-2 px-6 rounded-full"
              >
                Back to Learning Space
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (wallModal) {
    return (
      <div className="min-h-screen bg-black/60">
        <ActivityWallModal
          activityId={activityId}
          initialSubmissionId={wallModal}
          onClose={() => {
            navigate('/artist-wall', { replace: true });
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      {loading && hasLoadedOnce && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-30 bg-white/90 border border-amber-200 text-amber-800 text-xs px-3 py-1.5 rounded-full shadow">
          Refreshing activity...
        </div>
      )}
      <KidBackground variant="confetti" intensity={0.8} />
      <div className="absolute inset-0 bg-black/20" />
      
      <div className="relative z-10 p-6">
        <BackButton />
        
        <div className="max-w-7xl mx-auto mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Activity Details and Upload */}
            <div className="space-y-6">
              <ActivityDetailPane activity={activity} />
              
              {showUploadForm ? (
                <ArtworkUploadForm
                  activityId={activityId}
                  onUploadSuccess={handleUploadSuccess}
                  onUploadComplete={handleUploadComplete}
                />
              ) : (
                <div className="bg-gradient-to-br from-green-50/90 to-emerald-50/90 backdrop-blur-sm border border-green-200/60 rounded-2xl p-6 shadow-lg">
                  <div className="text-center">
                    <div className="text-6xl mb-4">🎉</div>
                    <h3 className="text-2xl font-bold text-green-800 mb-2">
                      Great Job!
                    </h3>
                    <p className="text-green-700 mb-4">
                      You've completed this activity! {userSubmission?.isPublic ? 'Your artwork is now shared with other artists.' : 'Your artwork is saved privately.'}
                    </p>
                    
                    {userSubmission && (
                      <div className="mb-4">
                        <img
                          src={userSubmission.imageUrl}
                          alt="Your artwork"
                          className="max-w-full max-h-48 mx-auto rounded-xl shadow-md"
                        />
                      </div>
                    )}
                    
                    <p className="text-sm text-green-600">
                      You can now view other artists' creations for this activity!
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Public Wall */}
            <div>
              {hasParticipated ? (
                <PublicWallCarousel activityId={activityId} />
              ) : (
                <div className="bg-gradient-to-br from-amber-50/90 to-orange-50/90 backdrop-blur-sm border border-amber-200/60 rounded-2xl p-6 shadow-lg">
                  <div className="text-center">
                    <div className="text-6xl mb-4">🔒</div>
                    <h3 className="text-xl font-bold text-amber-800 mb-2">
                      Complete the Activity First
                    </h3>
                    <p className="text-amber-700">
                      Upload your artwork for this activity to see what other young artists have created!
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Modal is rendered exclusively in the early return when wallModal is present */}
    </div>
  );
};

export default ActivityPage;

